import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddExistingBranchModal from './AddExistingBranchModal'
import AddEditCompany from './AddEditCompany'
import AddEditBranch from './AddEditBranch'
import ViewCompany from './ViewCompany'
import ChangeAdminModal from './ChangeAdminModal'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import ExpandableText from '../common/ExpandableText'
import ConfirmationModal from '../common/ConfirmationModal'
import { toast } from '../common/Toast'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, EnabledIcon, PendingIcon, DisabledIcon } from '../../icons'
import { loadAccounts } from '../../actions/accountsManagement'
import {
  loadCompanies,
  removeCompany,
  putCompany,
  convertOrganizationToIndividual,
  clearCompaniesMessages,
  loadCompaniesCountryOptions,
  convertOrganizationToCorporate,
} from '../../actions/companies'
import { clearBranchesMessages } from '../../actions/branches'
import { companiesDeletePermission, companiesWritePermission } from '../../utils/permissionValidation'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'

const orderingFields = [
  {
    field: 'companyName',
    order: 'asc',
    translationKey: 'organization_asc',
  },
  {
    field: 'companyName',
    order: 'desc',
    translationKey: 'organization_desc',
  },
  {
    field: 'email',
    order: 'asc',
    translationKey: 'admin_email_asc',
  },
  {
    field: 'email',
    order: 'desc',
    translationKey: 'admin_email_desc',
  },
  {
    field: 'officeAddressOne',
    order: 'asc',
    translationKey: 'address_asc',
  },
  {
    field: 'officeAddressOne',
    order: 'desc',
    translationKey: 'address_desc',
  },
  {
    field: 'account',
    order: 'asc',
    translationKey: 'account_asc',
  },
  {
    field: 'account',
    order: 'desc',
    translationKey: 'account_desc',
  },
  {
    field: 'members',
    order: 'asc',
    translationKey: 'members_asc',
  },
  {
    field: 'members',
    order: 'desc',
    translationKey: 'members_desc',
  },
  {
    field: 'status',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'status',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Companies = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [companiesTableData, setCompaniesTableData] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [countriesFilter, setCountriesFilter] = useState([])
  const [accountsFilter, setAccountsFilter] = useState([])
  const [statusesFilter, setStatusesFilter] = useState([])
  const [branches, setBranches] = useState([])
  const [accountType, setAccountType] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showAddEditCompanyModal, setShowAddEditCompanyModal] = useState(false)
  const [showViewCompanyModal, setShowViewCompanyModal] = useState(false)
  const [showAddBranchModal, setShowAddBranchModal] = useState(false)
  const [showAddExistingBranchModal, setShowAddExistingBranchModal] = useState(false)
  const [showChangeCompanyAdminModal, setShowChangeCompanyAdminModal] = useState(false)
  const [showConvertIndividualConfirmModal, setShowConvertIndividualConfirmModal] = useState(false)
  const [showConvertCorporateConfirmModal, setShowConvertCorporateConfirmModal] = useState(false)
  const [showDeleteCompanyConfirmModal, setShowDeleteCompanyConfirmModal] = useState(false)
  const [companyIdToConvert, setCompanyIdToConvert] = useState(null)
  const [companyIdToDelete, setCompanyIdToDelete] = useState(null)
  const [expandedCompany, setExpandedCompany] = useState(0)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const companyStatuses = ['approved', 'pending', 'disabled', 'rejected']
  const ACCOUNT_TYPE_MAP = {
    end_user: 'End User',
    dealer: 'Dealer',
  }

  const getApiAccountType = (accountTypeFromCompany) => {
    return ACCOUNT_TYPE_MAP[accountTypeFromCompany] || accountTypeFromCompany
  }

  const buildAddress = (company) => {
    let address = ''

    if (company.officeAddressOne) {
      address += `${company.officeAddressOne} `
    }

    if (company.officeAddressTwo) {
      address += `${company.officeAddressTwo} `
    }

    if (company.city) {
      address += `${company.city} `
    }

    if (company.state) {
      address += `${company.state} `
    }

    if (company.zipCode) {
      address += `${company.zipPostalCode} `
    }

    if (company.country) {
      address += `${company.country}`
    }

    return address.trimEnd()
  }

  useEffect(() => {
    if (props.countryOptions.length === 0) {
      props.actions.loadCompaniesCountryOptions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.accounts.length === 0 && !props.accountsLoading) {
      props.actions.loadAccounts()
    }
  }, [props.actions])

  useEffect(() => {
    const tableData = props.companies.map((company, index) => {
      return {
        index,
        identifier: company.id,
        company: company.companyName,
        email: company.email,
        address: buildAddress(company),
        account: company.account,
        isCorporation: company.isCorporation,
        members: company.branches?.reduce((acc, branch) => acc + branch.members, 0) || 0,
        branches: company.branches?.length || 0,
        status: company.status,
        actions: [
          ...(company.status === 'pending'
            ? [
                {
                  label: 'approve',
                  onActionClick: (e) => {
                    e.stopPropagation()
                    handleReviewCompany(company.id, 'approved')
                  },
                  requiredPermission: companiesWritePermission,
                },
                {
                  label: 'reject',
                  onActionClick: (e) => {
                    e.stopPropagation()
                    handleReviewCompany(company.id, 'rejected')
                  },
                  requiredPermission: companiesWritePermission,
                },
              ]
            : []),
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditCompany(company.id)
            },
            requiredPermission: companiesWritePermission,
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditCompany(company.id, true)
            },
            requiredPermission: companiesWritePermission,
          },
          ...(company.status === 'approved'
            ? [
                ...(!company.isCorporation
                  ? [
                      {
                        label: 'organization_context.change_admin',
                        onActionClick: (e) => {
                          e.stopPropagation()
                          handleOpenChangeAdmin(company.id)
                        },
                      },
                      {
                        label: 'organization_context.convert_to_individual',
                        onActionClick: (e) => {
                          e.stopPropagation()
                          showConvertIndividualConfirmationModal(company.id)
                        },
                        isDisabled: company.branches?.reduce((acc, branch) => acc + branch.members, 0) > 1,
                        requiredPermission: companiesWritePermission,
                      },
                      {
                        label: 'organization_context.convert_to_corporate_dso',
                        onActionClick: (e) => {
                          e.stopPropagation()
                          showConvertCorporateConfirmationModal(company.id)
                        },
                        requiredPermission: companiesWritePermission,
                      },
                    ]
                  : [
                      {
                        label: 'organization_context.add_new_branch',
                        onActionClick: (e) => {
                          e.stopPropagation()
                          handleAddBranch(company.id)
                        },
                        requiredPermission: companiesWritePermission,
                      },
                      {
                        label: 'organization_context.choose_existing_branch',
                        onActionClick: (e) => {
                          e.stopPropagation()
                          handleOpenAddExistingBranchModal(company)
                        },
                        requiredPermission: companiesWritePermission,
                      },
                    ]),
              ]
            : []),
          //A.S: Company remove is disabled for now
          // {
          //   label: "remove",
          //   onActionClick: (e) => { e.stopPropagation(); showConfirmationModal(company.id) },
          //   isDisabled: company.status !== "disabled",
          //   requiredPermission: companiesDeletePermission,
          // },
        ],
      }
    })

    setCompaniesTableData(tableData)
  }, [props.companies])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearCompaniesMessages()
      props.actions.clearBranchesMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearCompaniesMessages()
      props.actions.clearBranchesMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadCompanies(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadCompanies(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, countriesFilter, accountsFilter, statusesFilter, orderBy])

  const handleLoadCompanies = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadCompanies(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setCountriesFilter([])
    setAccountsFilter([])
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (countriesFilter.length > 0) {
      queryParams = { ...queryParams, country: countriesFilter }
    }

    if (accountsFilter.length > 0) {
      queryParams = { ...queryParams, account: accountsFilter }
    }

    if (statusesFilter.length > 0) {
      queryParams = { ...queryParams, status: statusesFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const renderCompanyStatus = (status) => {
    switch (status) {
      case companyStatuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('approved')}</p>
          </div>
        )
      case companyStatuses[1]:
        return (
          <div className="status-pending">
            <PendingIcon />
            <p>{t('pending')}</p>
          </div>
        )
      case companyStatuses[2]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('disabled')}</p>
          </div>
        )
      case companyStatuses[3]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('rejected')}</p>
          </div>
        )
      default:
        return null
    }
  }

  const handleOpenViewEditCompany = (companyId, isEditModal) => {
    const company = props.companies.find((company) => company.id === companyId)

    if (company) {
      setSelectedCompany(company)
      isEditModal ? setShowAddEditCompanyModal(true) : setShowViewCompanyModal(true)
    }
  }

  const handleOpenChangeAdmin = (companyId) => {
    const company = props.companies.find((company) => company.id === companyId)

    if (company) {
      setShowChangeCompanyAdminModal(true)
      setSelectedCompany(company)
    }
  }

  const handleAddBranch = (companyId) => {
    const company = props.companies.find((company) => company.id === companyId)

    if (company) {
      setShowAddBranchModal(true)
      setSelectedCompany(company)
    }
  }

  const handleOpenAddExistingBranchModal = (company) => {
    const apiAccountType = getApiAccountType(company.accountType)
    if (!apiAccountType) {
      toast.error(t('error.account_type'))
      return
    }

    setSelectedCompany(company)
    setAccountType(apiAccountType)
    setBranches([])
    setShowAddExistingBranchModal(true)
  }
  
  const handleSaveBranches = async (branches) => {
    const branchIds = branches.map((b) => ({ id: b.id }))
    const bodyParams = {
      branches: branchIds,
      companyName: selectedCompany.companyName.trim(),
      email: selectedCompany.email.trim(),
    }
    await props.actions.putCompany(selectedCompany.id, bodyParams)
  }

  const showConfirmationModal = (id) => {
    setCompanyIdToDelete(id)
    setShowDeleteCompanyConfirmModal(true)
  }

  const handleDeleteCompany = () => {
    setShowDeleteCompanyConfirmModal(false)
    props.actions.removeCompany(companyIdToDelete)
  }

  const showConvertIndividualConfirmationModal = (id) => {
    setCompanyIdToConvert(id)
    setShowConvertIndividualConfirmModal(true)
  }

  const handleConvertToIndividual = () => {
    setShowConvertIndividualConfirmModal(false)
    props.actions.convertOrganizationToIndividual(companyIdToConvert)
  }

  const showConvertCorporateConfirmationModal = (id) => {
    setCompanyIdToConvert(id)
    setShowConvertCorporateConfirmModal(true)
  }

  const handleConvertToCorporate = () => {
    setShowConvertCorporateConfirmModal(false)
    props.actions.convertOrganizationToCorporate(companyIdToConvert)
  }

  const handleReviewCompany = (companyId, newStatus) => {
    const company = props.companies.find((company) => company.id === companyId)

    const bodyParams = {
      ...company,
      status: newStatus,
    }

    if (company) {
      props.actions.putCompany(company.id, bodyParams)
    }
  }

  const closeModal = () => {
    setShowAddEditCompanyModal(false)
    setShowViewCompanyModal(false)
    setShowChangeCompanyAdminModal(false)
    setShowAddBranchModal(false)
    setShowAddExistingBranchModal(false)
    setSelectedCompany(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const handleSuccesfullBranchAdd = (companyId) => {
    setCompaniesTableData((prevData) =>
      prevData.map((item) => (item.identifier === companyId ? { ...item, members: item.members + 1, branches: item.branches + 1 } : item)),
    )
  }

  return (
    <Fragment>
      <div className="companies-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={countriesFilter.length > 0 || accountsFilter.length > 0 || statusesFilter.length > 0}
            counterBadge={countriesFilter.length + accountsFilter.length + statusesFilter.length}
          />
          <SimpleButton
            className="action-button"
            onClick={() => {
              setSelectedCompany(null)
              setShowAddEditCompanyModal(true)
            }}
          >
            {t('organization_context.add_organization')}
          </SimpleButton>
        </div>
        {isMobile && companiesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && companiesTableData.length > 0 && (
          <GenericTable
            data={companiesTableData}
            headers={[
              {
                title: t('organization'),
                orderKey: 'companyName',
              },
              {
                title: t('admin'),
                orderKey: 'email',
              },
              {
                title: t('address'),
                orderKey: 'officeAddressOne',
              },
              {
                title: t('account'),
                orderKey: 'account',
              },
              {
                title: t('organization_context.corporation_dso'),
              },
              {
                title: t('nav.members'),
                orderKey: 'members',
              },
              {
                title: t('organization_context.branches'),
              },
              {
                title: t('status'),
                orderKey: 'status',
              },
              {
                title: '',
              },
            ]}
            keys={['company', 'email', 'address', 'account', 'isCorporation', 'members', 'branches', 'status', 'action']}
            keyRenderer={{
              address: (item) => {
                return (
                  <div className="address-table-wrapper">
                    <ExpandableText text={item.address} />
                  </div>
                )
              },
              isCorporation: (item) => (item.isCorporation ? t('yes') : t('no')),
              branches: (item) => {
                return (
                  <div className="branches-table-wrapper">
                    <ExpandableText text={item.branches} />
                  </div>
                )
              },
              status: (item) => {
                return <div className="status-wrapper">{renderCompanyStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(company) => handleOpenViewEditCompany(company.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          companiesTableData.map((company, index) => (
            <div key={index} className="card" onClick={() => setExpandedCompany(index)}>
              <div className={'card-item' + (expandedCompany !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('organization')}</div>
                <div className={'card-item-body' + (expandedCompany !== index ? ' align-center' : '')}>
                  <div>{company.company}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={company.actions} isLoading={showSpinner} />
                    )}
                  </div>
                </div>
              </div>
              {expandedCompany === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('admin')}</div>
                    <div className="card-item-body">{company.email}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('address')}</div>
                    <div className="card-item-body">{company.address}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('account')}</div>
                    <div className="card-item-body">{company.account}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('organization_context.corporation_dso')}</div>
                    <div className="card-item-body">{company.isCorporation ? t('yes') : t('no')}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('nav.members')}</div>
                    <div className="card-item-body">{company.members}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('organization_context.branches')}</div>
                    <div className="card-item-body">{company.branches}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderCompanyStatus(company.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && companiesTableData.length === 0 && (
          <EmptyPageResults title={t('organization_context.no-companies-found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.countryOptions,
              input: countriesFilter,
              output: (filterCountries) => {
                setCountriesFilter(filterCountries)
              },
            },
            {
              name: t('account'),
              type: FilterType.multiSelect,
              dataset: props.accounts,
              input: accountsFilter,
              output: (filterAccounts) => {
                setAccountsFilter(filterAccounts)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: companyStatuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditCompanyModal && (
        <AddEditCompany selectedCompany={selectedCompany} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}
      {showViewCompanyModal && (
        <ViewCompany
          selectedCompany={selectedCompany}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditCompany={() => {
            setShowViewCompanyModal(false)
            setShowAddEditCompanyModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showAddBranchModal && (
        <AddEditBranch
          selectedCompany={selectedCompany}
          onSuccessCreateBranch={handleSuccesfullBranchAdd}
          closeModal={closeModal}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
        />
      )}
      {showAddExistingBranchModal && (
        <AddExistingBranchModal
          accountType={accountType}
          selectedBranches={branches}
          handleUpdateBranches={setBranches}
          companyId={selectedCompany.id}
          onSave={handleSaveBranches}
          closeModal={() => setShowAddExistingBranchModal(false)}
        />
      )}
      {showChangeCompanyAdminModal && (
        <ChangeAdminModal selectedCompany={selectedCompany} closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />
      )}
      {showConvertIndividualConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowConvertIndividualConfirmModal(false)}
          onAccept={() => handleConvertToIndividual()}
          onCancel={() => setShowConvertIndividualConfirmModal(false)}
          message={t('organization_context.confirm_convert_to_individual')}
          requiredPermission={companiesWritePermission}
        />
      )}
      {showConvertCorporateConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowConvertCorporateConfirmModal(false)}
          onAccept={() => handleConvertToCorporate()}
          onCancel={() => setShowConvertCorporateConfirmModal(false)}
          message={t('organization_context.confirm_convert_to_corporate')}
          requiredPermission={companiesWritePermission}
        />
      )}
      {showDeleteCompanyConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteCompanyConfirmModal(false)}
          onAccept={() => handleDeleteCompany()}
          onCancel={() => setShowDeleteCompanyConfirmModal(false)}
          message={t('organization_context.confirm_delete_company')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={companiesDeletePermission}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ companies, branches, accounts, users }) {
  return {
    companies: companies?.companies || [],
    countryOptions: companies?.countryOptions?.map((country) => country.countryCode) || [],
    accounts: accounts?.accounts?.map((a) => a.name) || [],
    accountsLoading: accounts?.accountsLoading,
    isLoading: companies?.isLoading || users?.usersLoading || branches?.isLoading,
    canLoadMore: companies?.canLoadMore,
    errorMessage: companies?.errorMessage || branches?.errorMessage,
    successMessage: companies?.successMessage || branches?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCompanies,
        loadAccounts,
        loadCompaniesCountryOptions,
        putCompany,
        removeCompany,
        convertOrganizationToIndividual,
        convertOrganizationToCorporate,
        clearCompaniesMessages,
        clearBranchesMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Companies)

